import {
  GroupDimension,
  PrimaryGroup,
  PurchaseOrder,
  SkuGroup,
  Status,
  StatusFilter,
  SubGroup,
} from '../types';
import {
  inTransitQty,
  nextPlanOf,
  skuGroupMatchesFilter,
  worstOf,
  worstOfSkuGroups,
  STATUS_RANK,
  getStatus,
} from './statusHelpers';

/**
 * Stable identity for a clubbed SKU row, independent of which dimension
 * (vendor/client) it's currently grouped under — used to persist "on hold"
 * state and expand/collapse state across a grouping-mode switch.
 */
export function getSkuGroupHoldKey(pos: PurchaseOrder[]): string {
  return pos
    .map((po) => po.poNumber)
    .sort()
    .join(',');
}

/**
 * Clubs POs that share the given key fields into one row. Defaults to just
 * `sku`, since callers already scope `pos` to a single client/location first.
 */
export function buildSkuGroups(
  pos: PurchaseOrder[],
  keyFields: (keyof PurchaseOrder)[] = ['sku'],
  heldKeys: Set<string> = new Set()
): SkuGroup[] {
  const map = new Map<string, PurchaseOrder[]>();
  pos.forEach((po) => {
    const key = keyFields.map((field) => String(po[field])).join('||');
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(po);
  });

  return Array.from(map.values())
    .map((group) => {
      const onHold = heldKeys.has(getSkuGroupHoldKey(group));

      const upcomingDates = group
        .map((po) => nextPlanOf(po.deliverySchedule))
        .filter((date): date is string => Boolean(date))
        .sort();
      const nextPlan = upcomingDates.length ? upcomingDates[0] : null;

      const nextPlanQty = nextPlan
        ? group.reduce(
            (sum, po) =>
              sum +
              po.deliverySchedule
                .filter((entry) => entry.date === nextPlan)
                .reduce((s, e) => s + e.qty, 0),
            0
          )
        : 0;

      const inTransitDates = group
        .flatMap((po) => po.inTransit.map((entry) => entry.dispatchDate))
        .sort();
      const inTransitLatestDate = inTransitDates.length
        ? inTransitDates[inTransitDates.length - 1]
        : null;
      const worstStatus: Status = onHold ? 'onhold' : getStatus(upcomingDates);
      return {
        sku: group[0].sku,
        unit: group[0].unit,
        pos: group,
        ordered: group.reduce((sum, po) => sum + po.orderedQuantity, 0),
        pending: group.reduce((sum, po) => sum + po.remainingQuantity, 0),
        inTransitTotal: group.reduce((sum, po) => sum + inTransitQty(po), 0),
        inTransitLatestDate,
        nextPlan,
        nextPlanQty,
        onHold,
        worstStatus,
      };
    })
    .sort((a, b) => STATUS_RANK[a.worstStatus] - STATUS_RANK[b.worstStatus]);
}

/**
 * Builds the two-level hierarchy shown in the UI:
 *   - vendor view: Vendor -> Client -> SKU
 *   - client view: Client -> Location -> SKU
 */
export function groupByPrimary(
  data: PurchaseOrder[],
  dimension: GroupDimension,
  heldKeys: Set<string> = new Set()
): PrimaryGroup[] {
  const primaryMap = new Map<string, PurchaseOrder[]>();
  data.forEach((po) => {
    const key = po[dimension];
    if (!primaryMap.has(key)) primaryMap.set(key, []);
    primaryMap.get(key)!.push(po);
  });

  const secondaryField: keyof PurchaseOrder =
    dimension === 'vendor' ? 'client' : 'city';

  return Array.from(primaryMap.entries())
    .map(([key, pos]) => {
      const secondaryMap = new Map<string, PurchaseOrder[]>();
      pos.forEach((po) => {
        const label = String(po[secondaryField]);
        if (!secondaryMap.has(label)) secondaryMap.set(label, []);
        secondaryMap.get(label)!.push(po);
      });

      const subGroups: SubGroup[] = Array.from(secondaryMap.entries())
        .map(([label, subPos]) => {
          const skuGroups = buildSkuGroups(subPos, ['sku'], heldKeys);
          return { label, skuGroups, worstStatus: worstOfSkuGroups(skuGroups) };
        })
        .sort((a, b) => {
          return (a.label ?? '').localeCompare(b.label ?? '', undefined, {
            sensitivity: 'base',
          });
        });

      const group: PrimaryGroup = {
        key,
        pos,
        skuCount: new Set(pos.map((po) => po.sku)).size,
        worstStatus: subGroups.length
          ? worstOfSkuGroups(subGroups.flatMap((s) => s.skuGroups))
          : 'done',
        subGroups,
      };
      return group;
    })
    .sort((a, b) => {
      return (a.key ?? '').localeCompare(b.key ?? '', undefined, {
        sensitivity: 'base',
      });
    });
}

export interface GroupFilterOptions {
  statusFilters: Set<StatusFilter>;
  inTransitOnly: boolean;
}

/**
 * Prunes an already-built Vendor/Client -> Sub -> SKU tree down to the SKU
 * rows matching the active filter chips, dropping any sub-group or top-level
 * group left with nothing in it. Runs after grouping (rather than filtering
 * raw POs beforehand) so hold state and clubbed totals are already resolved.
 */
export function filterGroups(
  groups: PrimaryGroup[],
  options: GroupFilterOptions
): PrimaryGroup[] {
  const { statusFilters, inTransitOnly } = options;
  const reviewingHold = statusFilters.has('onhold');
  const otherFilters = Array.from(statusFilters).filter((f) => f !== 'onhold');

  const matches = (sg: SkuGroup): boolean => {
    if (reviewingHold) return sg.onHold;
    if (sg.onHold) return false;
    if (inTransitOnly && sg.inTransitTotal <= 0) return false;
    if (otherFilters.length > 0) {
      let matchesAny = false;
      otherFilters.forEach((filter) => {
        if (skuGroupMatchesFilter(sg, filter)) matchesAny = true;
      });
      if (!matchesAny) return false;
    }
    return true;
  };

  return groups
    .map((group) => {
      const subGroups = group.subGroups
        .map((sub) => ({ ...sub, skuGroups: sub.skuGroups.filter(matches) }))
        .filter((sub) => sub.skuGroups.length > 0)
        .map((sub) => ({
          ...sub,
          worstStatus: worstOfSkuGroups(sub.skuGroups),
        }));

      const skuCount = new Set(
        subGroups.flatMap((sub) => sub.skuGroups.map((sg) => sg.sku))
      ).size;

      return {
        ...group,
        subGroups,
        skuCount,
        worstStatus: subGroups.length
          ? worstOfSkuGroups(subGroups.flatMap((s) => s.skuGroups))
          : ('done' as Status),
      };
    })
    .filter((group) => group.subGroups.length > 0);
}
